export const privacyPolicyContent = {
  eyebrow: "Legal",
  title: "Privacy Policy",
  intro:
    "This Privacy Policy applies to the Hailo Digital website, which is the primary website of Hailo Technologies LLC and is operated through Hailo Digital, its sole division.",
  effectiveDate: "April 28, 2026",
  sections: [
    {
      title: "1. Operator",
      paragraphs: [
        "Hailo Digital is the public operating brand and sole division of Hailo Technologies LLC, a limited liability company registered in the State of New Jersey, United States.",
        "This website is the primary website of Hailo Technologies LLC and is presented publicly under the Hailo Digital name. This Privacy Policy applies only to this website and to inquiries and engagements initiated through it.",
      ],
    },
    {
      title: "2. Information We Collect",
      paragraphs: [
        "We collect personal information when you choose to contact us through the website, request a quote, or enter into a paid service engagement with Hailo Technologies LLC.",
      ],
      items: [
        "Name",
        "Company name, if provided",
        "Email address",
        "Message content you submit",
        "Billing details required to invoice paid engagements, such as billing name, billing address, and tax identifier where applicable",
      ],
    },
    {
      title: "3. How We Use Information",
      items: [
        "To review and respond to inquiries",
        "To prepare quotes and engagement documents for paid services",
        "To invoice and receive payment for services rendered under a written engagement",
        "To communicate with you about your message, quote, or engagement",
        "To maintain reasonable business records related to inquiries, quotes, and engagements",
      ],
    },
    {
      title: "4. Payment Processing",
      paragraphs: [
        "We do not store full payment card details on this website. Payments for paid service engagements are processed by Stripe, Inc., a third-party payment processor.",
        "Stripe collects and processes payment card information directly under its own privacy policy and security standards. Limited payment confirmation data, such as the last four digits of a card or a transaction reference, may be retained by us for accounting and recordkeeping.",
      ],
    },
    {
      title: "5. Sharing of Information",
      paragraphs: [
        "We do not sell or rent your personal information. We share information only when reasonably necessary to operate the website, respond to your request, deliver services you have engaged us for, comply with legal obligations, or protect our rights.",
        "We share information with Stripe, Inc. solely for the purpose of processing payments for paid engagements, and with professional advisors (such as accountants and legal counsel) where reasonably required.",
      ],
    },
    {
      title: "6. Retention and Security",
      paragraphs: [
        "We retain contact submissions and engagement records for as long as reasonably necessary to review, respond, deliver services, meet recordkeeping and tax requirements, and maintain reasonable business records. We use reasonable measures to protect submitted information, but no website or email transmission can be guaranteed as completely secure.",
      ],
    },
    {
      title: "7. Your Choices",
      paragraphs: [
        "You may contact us at admin@hailodigital.com to request access, correction, or deletion of personal information you submitted through this website, subject to any legal, contractual, or recordkeeping requirements that apply to paid engagements.",
      ],
    },
    {
      title: "8. Children’s Privacy",
      paragraphs: [
        "This website is not directed to children under 13, and we do not knowingly collect personal information from children through this site.",
      ],
    },
    {
      title: "9. Contact",
      paragraphs: [
        "If you have questions about this Privacy Policy, contact Hailo Digital at admin@hailodigital.com. Hailo Technologies LLC is registered in the State of New Jersey, United States.",
      ],
    },
  ],
};

export const termsOfServiceContent = {
  eyebrow: "Legal",
  title: "Terms of Service",
  intro:
    "These Terms of Service govern access to and use of the Hailo Digital website, which is the primary website of Hailo Technologies LLC and is operated through Hailo Digital, its sole division.",
  effectiveDate: "April 28, 2026",
  sections: [
    {
      title: "1. Use of the Website",
      paragraphs: [
        "By accessing this website, you agree to use it only for lawful purposes and in a manner that does not interfere with the website, its operation, or other users.",
        "For clarity, this website is the primary website of Hailo Technologies LLC and is operated publicly under Hailo Digital, its sole division.",
      ],
    },
    {
      title: "2. Website and Services",
      paragraphs: [
        "This website provides information about Hailo Digital and the services offered by Hailo Technologies LLC.",
        "Hailo Technologies LLC offers paid services to clients, including custom software development and AI development services, under separately agreed engagement terms (such as a written quote, statement of work, or master services agreement). The website itself does not provide an automated checkout or self-service account portal; engagements are entered into through direct contact with Hailo Digital.",
      ],
    },
    {
      title: "3. Services",
      paragraphs: [
        "Hailo Technologies LLC, operating as Hailo Digital, offers the following services to business clients:",
      ],
      items: [
        "Custom software development — bespoke software systems, web platforms, and internal tools designed and engineered to client requirements",
        "AI development services — artificial intelligence systems, integrations, and applied machine learning workflows for digital products",
      ],
    },
    {
      title: "4. Pricing and Engagements",
      paragraphs: [
        "Pricing is provided on a per-engagement basis. Prospective clients should contact admin@hailodigital.com to request pricing or a custom quote.",
        "All paid engagements are governed by a separate written agreement between the client and Hailo Technologies LLC, which sets out scope, fees, deliverables, timeline, and payment terms. These Terms of Service supplement, and do not replace, that engagement agreement.",
      ],
    },
    {
      title: "5. Payment Processing",
      paragraphs: [
        "Payments for paid services are processed by Stripe, Inc. By submitting payment, the client agrees to Stripe’s applicable terms of service. Hailo Technologies LLC does not store full payment card details on this website.",
        "Charges may appear on a payment statement as “HAILO TECHNOLOGIES” or a similar identifier reflecting the legal entity Hailo Technologies LLC.",
      ],
    },
    {
      title: "6. Refund Policy",
      paragraphs: [
        "Refunds for paid services are reviewed on a case-by-case basis at the sole discretion of Hailo Technologies LLC, taking into account work already performed, time invested, third-party costs incurred, and the terms of the underlying engagement agreement.",
        "Refund requests must be submitted in writing to admin@hailodigital.com within 30 days of the relevant invoice or charge. Full details are set out in our Refund Policy.",
      ],
    },
    {
      title: "7. Contact Submissions",
      paragraphs: [
        "If you contact us through the website, you agree to provide accurate information and not submit unlawful, misleading, abusive, or harmful content.",
      ],
    },
    {
      title: "8. Intellectual Property",
      paragraphs: [
        "All content on this website, including text, branding, design, graphics, and layout, is owned by or used with permission by Hailo Digital or Hailo Technologies LLC and may not be copied, reproduced, or used without permission.",
      ],
    },
    {
      title: "9. Third-Party Links",
      paragraphs: [
        "This website may contain links to third-party or external websites. We are not responsible for the content, policies, or practices of external sites.",
      ],
    },
    {
      title: "10. No Warranty",
      paragraphs: [
        "This website is provided on an “as is” and “as available” basis without warranties of any kind, whether express or implied, to the fullest extent permitted by law.",
      ],
    },
    {
      title: "11. Limitation of Liability",
      paragraphs: [
        "To the fullest extent permitted by law, Hailo Digital and Hailo Technologies LLC will not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to use of this website.",
      ],
    },
    {
      title: "12. Changes to These Terms",
      paragraphs: [
        "We may update these Terms of Service from time to time. Updated terms will be posted on this page with a revised effective date.",
      ],
    },
    {
      title: "13. Governing Law",
      paragraphs: [
        "These Terms of Service are governed by the laws of the State of New Jersey, United States, without regard to conflict of law principles.",
      ],
    },
    {
      title: "14. Contact",
      paragraphs: [
        "For questions about these Terms of Service, contact admin@hailodigital.com. Hailo Technologies LLC is registered in the State of New Jersey, United States.",
      ],
    },
  ],
};

export const refundPolicyContent = {
  eyebrow: "Legal",
  title: "Refund Policy",
  intro:
    "This Refund Policy describes how Hailo Digital, the sole division of Hailo Technologies LLC, handles refund requests for paid service engagements. Hailo Digital is the public operating brand and primary website of Hailo Technologies LLC.",
  effectiveDate: "April 28, 2026",
  sections: [
    {
      title: "1. Scope",
      paragraphs: [
        "This Refund Policy applies to paid service engagements entered into with Hailo Technologies LLC, including custom software development and AI development services.",
        "Free informational use of this website is not subject to refunds because no payment is taken for browsing the site.",
      ],
    },
    {
      title: "2. Refund Decisions",
      paragraphs: [
        "Refund requests are reviewed on a case-by-case basis at the sole discretion of Hailo Technologies LLC. The following factors are considered:",
      ],
      items: [
        "The amount of work already performed and time invested",
        "Third-party costs already incurred on behalf of the client",
        "The stage of the engagement when the request is made",
        "The specific terms of the underlying engagement agreement, statement of work, or written quote",
      ],
    },
    {
      title: "3. How to Request a Refund",
      paragraphs: [
        "To request a refund, email admin@hailodigital.com with the following information:",
      ],
      items: [
        "Client name and company",
        "Engagement reference, statement of work, or quote number",
        "Invoice number and date of the relevant charge",
        "Reason for the refund request",
      ],
    },
    {
      title: "4. Time Window",
      paragraphs: [
        "Refund requests must be submitted in writing within 30 days of the relevant invoice or charge. Requests submitted after 30 days may not be considered.",
      ],
    },
    {
      title: "5. Non-Refundable Items",
      paragraphs: [
        "The following are generally not refundable:",
      ],
      items: [
        "Work already performed and delivered to the client",
        "Custom software, AI systems, or other deliverables already produced and accepted",
        "Third-party costs and fees already incurred on the client’s behalf",
        "Retainer or engagement fees that have already been consumed in performed work",
      ],
    },
    {
      title: "6. Refund Method and Timing",
      paragraphs: [
        "Approved refunds are issued via the original payment method through Stripe, Inc., the payment processor used by Hailo Technologies LLC. Refunds typically appear on the original payment method within 5–10 business days of approval, depending on the issuing bank.",
      ],
    },
    {
      title: "7. Chargebacks and Disputes",
      paragraphs: [
        "Clients are asked to contact admin@hailodigital.com first to resolve any concerns before initiating a chargeback or dispute. Hailo Technologies LLC will address disputed charges in good faith and provide supporting engagement and delivery records as needed.",
      ],
    },
    {
      title: "8. Statement Descriptor",
      paragraphs: [
        "Charges from Hailo Technologies LLC may appear on a payment statement as “HAILO TECHNOLOGIES” or a similar identifier that reflects the legal entity. This helps clients recognize charges associated with Hailo Digital and Hailo Technologies LLC.",
      ],
    },
    {
      title: "9. Contact",
      paragraphs: [
        "For all refund-related questions and requests, contact admin@hailodigital.com. Hailo Technologies LLC is registered in the State of New Jersey, United States.",
      ],
    },
  ],
};
